import { Table } from "components/table";
import { ITableHeadDTO } from "data/dtos/components/i-table-head-dto";
import { IOrganizationDTO } from "data/dtos/security/i-organization-dto";
import React, { ChangeEvent, MouseEvent, useEffect, useState } from "react";
import api from "services/api";
import { useAlreadyMounted } from "utils/use-already-mounted";

const headCells: ITableHeadDTO[] = [
  {
    id: "organization",
    label: "Oraganização",
    width: 4,
    type: "group",
    groupText: {
      title: "corporateName",
      subTitle: "federalTaxId",
    },
  },
  {
    id: "corporateName",
    label: "Razão Social",
    width: 3,
    type: "text",
  },
  {
    id: "tradingName",
    label: "Nome Fantasia",
    width: 3,
    type: "text",
  },
];

const OrganizationList: React.FC = () => {
  const [page, setPage] = useState<number>(0);
  const [search, setSearch] = useState<string>("");
  const [rowsCount, setRowsCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [rowsPerPage, setRowsPerPage] = useState<number>(100);
  const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);
  const [columnOrder, setColumnOrder] = useState<("ASC" | "DESC")[]>([]);
  const [recordToDelete, setRecordToDelete] = useState<string | null>("");
  const [orderByDirection, setOrderByDirection] = useState<boolean>(true);
  const [organizationsList, setOrganizationsList] = useState<
    IOrganizationDTO[]
  >([]);

  const alreadyMounted = useAlreadyMounted();

  const loadOrganizations = async (isSearch?: boolean) => {
    if (!isSearch) {
      setIsLoading(true);
    }

    await api
      .post("/organizations/list", { search, page, rowsPerPage, columnOrder })
      .then((response) => setOrganizationsList(response.data.data))
      .catch((error) => {
        setIsLoading(false);
        console.log(error);
      });

    await api
      .post("/organizations/count", { search })
      .then((response) => setRowsCount(response.data.data.count))
      .catch((error) => {
        setIsLoading(false);
        console.log(error);
      });

    setIsLoading(false);
  };

  const handleDelete = async () => {
    await api
      .delete(`/organizations/${recordToDelete}`)
      .then(async () => await loadOrganizations())
      .catch((error) => console.log(error));
  };

  const searchChange = (value: string) => {
    setSearch(value);
  };

  const handleClose = () => {
    setModalIsOpen(false);
  };

  const handleChangePage = (
    event: MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    event!.preventDefault();
    setPage(newPage);
  };

  const handleChangeRowsPerPage = async (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCloseDelete = () => {
    setModalIsOpen(false);
    handleDelete();
  };

  const handleDeleteButton = (e: MouseEvent<any, any>, id: string) => {
    e.stopPropagation();
    setModalIsOpen(true);
    setRecordToDelete(id);
  };

  const handleSortChange = (index: number) => {
    columnOrder[index] = columnOrder[index] === "ASC" ? "DESC" : "ASC";

    setColumnOrder(columnOrder);
    setOrderByDirection(!orderByDirection);
  };

  const tabOptions = [
    {
      title: "Todas",
      size: rowsCount,
      handleClick: () => {},
    },
  ];

  useEffect(() => {
    loadOrganizations();
  }, [orderByDirection, rowsPerPage, page]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!alreadyMounted) {
        loadOrganizations(true);
      }
    }, 1000);

    return () => clearTimeout(timeout);
  }, [search]);

  return (
    <Table.Root>
      <Table.Info
        title="Organizações"
        route="/organizations/new"
        subTitle="Gerencie as permissões de acesso e convide novas empresas para acessar a plataforma."
      />

      <Table.tabs options={tabOptions} />
      <Table.SearchBar value={search} handleChange={searchChange} />

      <Table.Overlay
        isEmpty={organizationsList.length <= 0}
        isSearch={search.length > 0}
        isLoading={false}
        route="/organizations/new"
        emptyRouteText="Criar organização"
        searchRouteText="Criar organização"
        emptyMessage="Seja bem-vindo à página de gerenciamento de organizações. Até o momento, nenhuma organização foi criada. Para começar, você pode criar uma nova organização clicando no botão"
        searchMessage="Não encontramos nenhuma organização com o termo de busca, você pode criar uma nova organização clicando no botão"
      >
        <Table.Wrapper
          totalRows={rowsCount}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          rowsPerPage={rowsPerPage}
          handleChangePage={handleChangePage}
          page={page}
          handleClose={handleClose}
          handleCloseDelete={handleCloseDelete}
          modalIsOpen={modalIsOpen}
        >
          <Table.Header
            handleSortChange={handleSortChange}
            setOrder={setColumnOrder}
            headCells={headCells}
            order={columnOrder}
          />
          <Table.Body
            handleDeleteButton={handleDeleteButton}
            editRoutePath="/organizations/edit"
            headCells={headCells}
            rows={organizationsList}
          />
        </Table.Wrapper>
      </Table.Overlay>
    </Table.Root>
  );
};

export default OrganizationList;
